import { useEffect, useState } from 'react';
import { Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title } from '@tremor/react';
import type { ServiceCommissionRow } from '../../api/types';
import { fetchServiceCommission } from '../../api/service';
import { t } from '../../lib/i18n';

export interface TechRow extends ServiceCommissionRow { efficiency: number | null; }

/**
 * Pure, render-free helper (vitest-tested directly). Merges each technician's
 * window accrual + ss-tech ledger balances (already both on ServiceCommissionRow)
 * with a derived efficiency = estimated / actual hours, then sorts by accrued
 * commission descending. efficiency is null when there are no actual (or no
 * estimated) hours to compare, so the view shows "—" rather than a bogus ratio.
 */
export function commissionRows(rows: ServiceCommissionRow[]): TechRow[] {
  return rows
    .map((r) => ({ ...r, efficiency: r.act_hrs > 0 && r.est_hrs > 0 ? r.est_hrs / r.act_hrs : null }))
    .sort((a, b) => b.accrued_mxn - a.accrued_mxn);
}

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 });
const fmtHrs = new Intl.NumberFormat('es-MX', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * Técnicos view — per-technician productivity (jobs, estimated vs. actual
 * hours, efficiency, labor revenue) joined to the real accrued-vs-paid
 * commission ledger (ss-comm accrual + ss-tech com/comdue/compaid balances).
 * Reads the same from/to/sid window as the rest of the cockpit; commission is
 * its own cached daemon verb, so this is the one view that fetches on its own.
 */
export default function Tecnicos({ from, to, sid }: { from: string; to: string; sid: number | null }) {
  const [rows, setRows] = useState<ServiceCommissionRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let a = true;
    setRows(null);
    setErr(null);
    fetchServiceCommission({ from, to, sid })
      .then((r) => { if (a) setRows(r); })
      .catch((e: Error) => { if (a) setErr(e.message); });
    return () => { a = false; };
  }, [from, to, sid]);

  const c = t.service.cockpit;
  const tc = t.service.techCols;
  if (err) return <Card><Text>{err}</Text></Card>;
  if (!rows) return <Card><Text>…</Text></Card>;
  const list = commissionRows(rows);
  return (
    <Card>
      <Title className="text-base">{c.commissionTitle}</Title>
      <Table className="mt-2">
        <TableHead>
          <TableRow>
            <TableHeaderCell>{tc.tech}</TableHeaderCell>
            <TableHeaderCell className="text-right">{tc.jobs}</TableHeaderCell>
            <TableHeaderCell className="text-right">{tc.estHrs}</TableHeaderCell>
            <TableHeaderCell className="text-right">{tc.actHrs}</TableHeaderCell>
            <TableHeaderCell className="text-right">{tc.eff}</TableHeaderCell>
            <TableHeaderCell className="text-right">{tc.revenue}</TableHeaderCell>
            <TableHeaderCell className="text-right">{c.accrued}</TableHeaderCell>
            <TableHeaderCell className="text-right">{c.due}</TableHeaderCell>
            <TableHeaderCell className="text-right">{c.paid}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list.map((r) => (
            <TableRow key={`${r.sid}-${r.tech}`}>
              <TableCell>{r.tech_name || r.tech}</TableCell>
              <TableCell className="text-right tabular-nums">{r.jobs}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtHrs.format(r.est_hrs)}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtHrs.format(r.act_hrs)}</TableCell>
              <TableCell className="text-right tabular-nums">{r.efficiency == null ? '—' : `${Math.round(r.efficiency * 100)}%`}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtMxn.format(r.revenue_mxn)}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtMxn.format(r.accrued_mxn)}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtMxn.format(r.comdue)}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtMxn.format(r.compaid)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
